// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, User, Phone, Mail, MessageSquare, Calendar } from 'lucide-react';
import '../../styles/guest-info-form.css';

interface GuestInfoFormProps {
  value: {
    name: string;
    phone: string;
    email: string;
    notes: string;
    birthdate: string;
  };
  onChange: (data: Partial<{ name: string; phone: string; email: string; notes: string; birthdate: string }>) => void;
  onNext: () => void;
  onBack: () => void;
}

const GuestInfoForm: React.FC<GuestInfoFormProps> = ({
  value,
  onChange,
  onNext,
  onBack
}) => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const birthdateRef = useRef<HTMLInputElement>(null);

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 2) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
    if (cleaned.length <= 10) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
  };

  const validateField = (name: string, value: string) => {
    let error = '';
    switch (name) {
      case 'name':
        if (!value.trim()) error = 'Nome é obrigatório';
        else if (value.trim().length < 2) error = 'Nome deve ter pelo menos 2 caracteres';
        break;
      case 'phone':
        if (!value.trim()) error = 'Telefone é obrigatório';
        else if (!/^\(\d{2}\)\s\d{4,5}-\d{4}$/.test(value)) error = 'Formato inválido (ex: (11) 99999-9999)';
        break;
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'E-mail inválido';
        break;
      case 'birthdate':
        if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) error = 'Data inválida';
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === 'phone') {
      finalValue = formatPhone(value);
    }
    onChange({ [name]: finalValue });
    validateField(name, finalValue);
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) {
      validateField('name', value.name);
      validateField('phone', value.phone);
      validateField('email', value.email);
      validateField('birthdate', value.birthdate);
      if (errors.name) nameRef.current?.focus();
      else if (errors.phone) phoneRef.current?.focus();
      else if (errors.email) emailRef.current?.focus();
      else if (errors.birthdate) birthdateRef.current?.focus();
      return;
    }
    onNext();
  };
  
  const isFormValid = () => {
    return value.name.trim() !== '' && value.phone.trim() !== '' && Object.values(errors).every(e => !e);
  };
  
  return (
    <form className="guest-info-form" onSubmit={handleSubmit}>
      <div className="form-fields">
        <div className="form-group">
          <label htmlFor="name">
            <User size={18} />
            Nome Completo
          </label>
          <input
            ref={nameRef}
            id="name"
            name="name"
            type="text"
            value={value.name}
            onChange={handleChange}
            placeholder="Digite seu nome completo"
            required
          />
          {errors.name && <span className="error">{errors.name}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="phone">
            <Phone size={18} />
            Telefone
          </label>
          <input
            ref={phoneRef}
            id="phone"
            name="phone"
            type="tel"
            value={value.phone}
            onChange={handleChange}
            placeholder="Digite seu telefone"
            required
          />
          {errors.phone && <span className="error">{errors.phone}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="email">
            <Mail size={18} />
            E-mail (opcional)
          </label>
          <input
            ref={emailRef}
            id="email"
            name="email"
            type="email"
            value={value.email}
            onChange={handleChange}
            placeholder="Digite seu e-mail"
          />
          {errors.email && <span className="error">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="birthdate">
            <Calendar size={18} />
            Data de Nascimento (opcional)
          </label>
          <input
            ref={birthdateRef}
            id="birthdate"
            name="birthdate"
            type="date"
            value={value.birthdate}
            onChange={handleChange}
            max={new Date().toISOString().split('T')[0]}
          />
          {errors.birthdate && <span className="error">{errors.birthdate}</span>}
        </div>
        
        <div className="form-group full-width">
          <label htmlFor="notes">
            <MessageSquare size={18} />
            Observações (opcional)
          </label>
          <textarea
            id="notes"
            name="notes"
            value={value.notes}
            onChange={handleChange}
            placeholder="Alguma observação ou restrição alimentar?"
            rows={4}
          />
        </div>
      </div>
      
      <div className="form-actions">
        <button type="button" className="btn-back" onClick={onBack}>
          <ChevronLeft size={18} />
          Voltar
        </button>
        
        <button 
          type="submit" 
          className="btn-next"
          disabled={!isFormValid()}
        >
          Revisar
          <ChevronRight size={18} />
        </button>
      </div>
    </form>
  );
};

export default GuestInfoForm;
